import { RiFileExcelLine } from 'react-icons/ri'

import { notificationService } from '@/Core'
import { serverMessage } from '@/Ship/Helpers/serverError'
import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'

import { ExportCrmListTask } from './ExportCrmListTask'

import type { ExportListContextLike } from './ExportCrmListTask'

/** Ключ кнопки в `onButtons` шапки реєстру. */
export const EXPORT_BUTTON_KEY = 'exportToExcel'

/** Підпис кнопки (D-23: у легасі — «Експорт в Exel»). */
export const EXPORT_BUTTON_LABEL = 'Експорт в Excel'

interface ListWithPermissions extends ExportListContextLike {
    getState?: (path: string) => unknown
}

interface HeaderButtonsEvent {
    buttons: Record<string, unknown>
    crudContext?: { listContext?: ListWithPermissions; contextType?: string; resource?: { key?: string } } | null
}

/**
 * Чи можна вивантажувати: колекційне право `export` з `_permissions`
 * відповіді списку (`ListConfig` кладе його в стан списку). Поки список ще не
 * відповів — те саме право з `/services/auth/permissions` (той самий
 * `getCollectionPermissions()` бека). Ролей фронт не питає (`SPEC-003` §1.3).
 */
export const canExport = (
    listContext: ListWithPermissions | undefined,
    resourceKey?: string,
    permission = 'export'
): boolean => {
    const permissions = listContext?.getState?.('_permissions') as Record<string, unknown> | undefined

    if (permissions && typeof permissions === 'object' && permission in permissions) return permissions[permission] === true

    return resourceKey ? CheckGlobalResourceAccessTask(resourceKey, permission) : false
}

/**
 * Обробник `headerPage.eventHandlers.onButtons` для реєстру CRM: кнопка
 * «Експорт в Excel» з іконкою легасі (`RiFileExcelLine`) у шапці списку.
 *
 * Вивантажує поточну вибірку таблиці ({@see ExportCrmListTask}); поки бек
 * формує файл, видно індикатор, помилку сервера показує `serverMessage`.
 *
 * `permission` — колекційне право, за яким видно кнопку: `export` (типово) або
 * вужче право реєстру (`export_registry` заявок — лише адміністратор, Q2.6).
 */
export const exportToExcelButton =
    (fileName: string, permission = 'export') =>
    ({ buttons, crudContext }: HeaderButtonsEvent): void => {
        const listContext = crudContext?.listContext

        // Вкладки карток (`contextType: 'detail'`) вивантаження не мають
        if (!listContext || crudContext?.contextType === 'detail') return

        buttons[EXPORT_BUTTON_KEY] = {
            label: EXPORT_BUTTON_LABEL,
            icon: <RiFileExcelLine style={{ fontSize: 20 }} />,
            sort: 90,
            priority: 'button',
            size: 'middle',
            depFn: () => canExport(listContext, crudContext?.resource?.key, permission),
            onClick: () => {
                notificationService.showLoader('Формуємо файл…')

                ExportCrmListTask(listContext, fileName)
                    .catch((error: unknown) => {
                        notificationService.showError('Не вдалося вивантажити', serverMessage(error, 'Спробуйте ще раз.'))
                    })
                    .finally(() => {
                        notificationService.hideLoader()
                    })
            },
        }
    }
